<script setup lang="ts">
import { PromotionType } from '@rediredi/types'
import RInputField from '../base/RInputField.vue'

const { t } = useI18n()
const { event } = useEvent()
const discountStore = useDiscountStore()
const { open, close, isOpen } = useModalState()
const { discountStorage } = useDiscountStorage()
const { isLoading, startLoading, stopLoading } = useLoadingState()

const state = reactive({
  coupon: '',
  isApplied: false,
  isCouponInvalid: false,
})

const couponInputRef = ref<InstanceType<typeof RInputField>>()

const applyCoupon = async () => {
  try {
    startLoading()
    await discountStore.applyCoupon(state.coupon)
    state.isApplied = true
    state.isCouponInvalid = false
    event('product_catalog_coupon_applied')
  } catch (e) {
    discountStore.coupon = null
    discountStorage.value.code = ''
    state.isApplied = false
    state.isCouponInvalid = true
  } finally {
    stopLoading()
  }
}

const content = computed(() => {
  const defaultContent = {
    title: t('components.couponApplyModal.title'),
    text: t('components.couponApplyModal.text'),
  }

  if (!state.isApplied) return defaultContent

  if (!discountStore.coupon) return defaultContent

  if (!discountStore.isPromoValid(discountStore.coupon)) return defaultContent

  if (discountStore.coupon.type === PromotionType.Discount) {
    return {
      title: t('components.couponApplyModal.appliedTitle', {
        code: discountStore.coupon?.code,
      }),
      text: t('components.couponApplyModal.applied.percentage.text'),
    }
  }

  if (discountStore.coupon.type === PromotionType.SellingPrice) {
    return {
      title: t('components.couponApplyModal.appliedTitle', {
        code: discountStore.coupon?.code,
      }),
      text: t('components.couponApplyModal.applied.price.text', {
        discountAmount: formatPrice(discountStore.coupon?.promotionPrice?.amount || 0, {
          currency: discountStore.coupon?.promotionPrice?.currency || 'BRL',
        }),
      }),
    }
  }

  if (discountStore.coupon.type === PromotionType.FreeShipping) {
    return {
      title: t('components.couponApplyModal.appliedTitle', {
        code: discountStore.coupon?.code,
      }),
      text: t('components.couponApplyModal.applied.freeShipping.text'),
    }
  }

  return defaultContent
})

const appliedCouponLabel = computed(() => {
  if (!discountStore.coupon) return ''

  if (discountStore.coupon.type === PromotionType.Discount) {
    return t('components.couponForm.appliedCouponLabel.discountPercentage', {
      code: discountStore.coupon?.code,
      discountPercentage: discountStore.coupon?.promotionPercent,
    })
  }

  if (discountStore.coupon.type === PromotionType.SellingPrice) {
    return t('components.couponForm.appliedCouponLabel.discountPrice', {
      code: discountStore.coupon?.code,
      discountPrice: formatPrice(discountStore.coupon?.promotionPrice?.amount || 0, {
        currency: discountStore.coupon?.promotionPrice?.currency || 'BRL',
      }),
    })
  }

  if (discountStore.coupon.type === PromotionType.FreeShipping) {
    return t('components.couponForm.appliedCouponLabel.freeShipping', {
      code: discountStore.coupon?.code,
    })
  }
})

const removeCoupon = () => {
  discountStore.removeCoupon()
  state.coupon = ''
  state.isApplied = false
  state.isCouponInvalid = false
  event('product_catalog_coupon_removed')
}

watch(isOpen, async (newVal) => {
  if (newVal && discountStore.coupon && discountStore.isPromoValid(discountStore.coupon)) {
    state.coupon = discountStore.coupon?.code || ''
    state.isApplied = true
    state.isCouponInvalid = false
  }

  if (newVal && !state.isApplied) {
    await nextTick()
    couponInputRef.value?.inputEl?.focus()
  }
})

defineExpose({
  open,
  close,
  isOpen,
})
</script>

<template>
  <RBaseModal
    is-closeable
    is-closeable-on-esc
    class="!w-[490px] px-6 pb-8 pt-4 shadow-xl"
    :is-open="isOpen"
    @close="close"
  >
    <div class="flex flex-col gap-6 text-center">
      <h1 class="primary-text text-xl font-bold">
        {{ content.title }}
      </h1>

      <p class="text-gray-400">
        {{ content.text }}
      </p>

      <div>
        <RInputField
          v-if="!state.isApplied"
          ref="couponInputRef"
          v-model="state.coupon"
          :class="{
            invalid: state.isCouponInvalid,
          }"
          upper-case
          :apply-regex="/ |[^A-Za-z0-9]/g"
          :readonly="state.isApplied"
          :input-el-attrs="{
            placeholder: t('components.couponForm.placeholder'),
            maxlength: 30,
          }"
          class="uppercase"
          @input="state.isCouponInvalid = false"
          @keyup.enter="applyCoupon"
        >
          <template #prepended-icon>
            <RIcon
              class="icon"
              size="20"
              name="material-symbols:local-activity-outline-rounded"
            ></RIcon>
          </template>
        </RInputField>

        <template v-else>
          <div
            class="applied flex min-h-[48px] items-center gap-2 rounded-full bg-apple px-4 py-2 text-left uppercase text-white"
          >
            <RIcon
              class="icon text-white"
              size="20"
              name="material-symbols:local-activity-outline-rounded"
            ></RIcon>
            <div>
              {{ appliedCouponLabel }}
            </div>
          </div>
        </template>

        <div v-if="state.isCouponInvalid" class="mt-1 w-full text-left text-[12px] text-red-200">
          {{ t('components.couponForm.invalidError') }}
        </div>
      </div>

      <div class="flex items-center justify-center gap-2">
        <RBaseButton
          v-if="!state.isApplied"
          :full-width="false"
          color="primary"
          class="min-w-[160px] px-6"
          :loading="isLoading"
          @click="applyCoupon"
        >
          {{ t('components.couponApplyModal.applyButton') }}
        </RBaseButton>

        <template v-else>
          <RBaseButton
            :full-width="false"
            color="secondary"
            class="px-6 sm:min-w-[160px]"
            :loading="isLoading"
            @click="removeCoupon"
          >
            {{ t('components.couponApplyModal.removeButton') }}
          </RBaseButton>
          <RBaseButton
            :full-width="false"
            color="primary"
            class="px-6 sm:min-w-[160px]"
            :loading="isLoading"
            @click="close"
          >
            {{ t('components.couponApplyModal.close') }}
          </RBaseButton>
        </template>
      </div>
    </div>
  </RBaseModal>
</template>

<style scoped>
.invalid {
  :deep(.input-el) {
    border-color: #fa4b4b;
  }
}
</style>
